import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ArrowRight } from 'lucide-react';
import api, { trackEvent } from '@/lib/api';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_feasibility-hub-10/artifacts/nb8rpfwp_Ruam%20Architects%20Logo.jpg";

const VALUES = [
  { title: 'Context First', description: 'Every project starts with the site — its orientation, its climate, its neighbourhood. Architecture that ignores context creates buildings, not spaces.' },
  { title: 'Material Honesty', description: 'We believe materials should be what they are. Concrete, timber, stone, glass — each chosen for purpose, expressed without pretence.' },
  { title: 'Light as Material', description: 'Natural light is the most powerful design element. We shape space around how light moves through a building, across seasons and hours.' },
  { title: 'Restraint Over Excess', description: 'Good architecture is as much about what you leave out as what you put in. We pursue clarity, not complexity for its own sake.' },
];

// Render multi-paragraph text from the editor by splitting on blank lines.
const Paragraphs = ({ text }) => (
  <>
    {(text || '').split(/\n{2,}/).map((para, i) => (
      <p key={i}>{para}</p>
    ))}
  </>
);

export default function About() {
  const [about, setAbout] = useState(null);

  useEffect(() => {
    trackEvent('page_view', { page: 'about' });
    api.get('/about').then(r => setAbout(r.data)).catch(() => setAbout({}));
  }, []);

  // Render only after fetch resolves so editor changes show up immediately on
  // the public site without flashing the static fallback.
  if (!about) return <div className="min-h-screen bg-brand-light pt-20" data-testid="about-page" />;

  return (
    <div className="min-h-screen bg-brand-light pt-20" data-testid="about-page">
      {/* Hero */}
      <div className="section-dark py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <img src={LOGO_URL} alt="Raum Architects" className="w-16 h-16 rounded-full object-cover ring-2 ring-brand-accent/40 shadow-lg" />
            <div>
              <h1 className="font-heading text-3xl md:text-4xl tracking-tighter font-medium text-white" data-testid="about-title">{about.title}</h1>
              <p className="text-brand-accent font-heading text-sm italic tracking-wide mt-1" data-testid="about-tagline">{about.tagline}</p>
            </div>
          </div>
          <p className="text-white/60 font-body text-base md:text-lg leading-relaxed max-w-3xl" data-testid="about-intro">
            {about.intro_text}
          </p>
        </div>
      </div>

      {/* Philosophy */}
      <section className="bg-white py-20 px-6" data-testid="about-philosophy">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Philosophy</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-brand-dark mb-6">
            {about.philosophy_heading}
          </h2>
          <div className="font-body text-brand-dark/60 text-base leading-relaxed space-y-4 max-w-3xl" data-testid="about-philosophy-text">
            <Paragraphs text={about.philosophy_text} />
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Director & Team */}
      {(about.director_text || about.team_text) && (
        <section className="bg-white py-20 px-6" data-testid="about-people">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
            {about.director_text && (
              <div data-testid="about-director">
                <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Direction</p>
                <div className="font-body text-brand-dark/60 text-base leading-relaxed space-y-4">
                  <Paragraphs text={about.director_text} />
                </div>
              </div>
            )}
            {about.team_text && (
              <div data-testid="about-team">
                <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Team & Network</p>
                <div className="font-body text-brand-dark/60 text-base leading-relaxed space-y-4">
                  <Paragraphs text={about.team_text} />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <Separator className="max-w-4xl mx-auto" />

      {/* Values */}
      <section className="bg-white py-20 px-6" data-testid="about-values">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Design Principles</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-brand-dark mb-10">
            What Guides Our Work
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="border-l-2 border-brand-accent/30 pl-5"
                data-testid={`value-${i}`}
              >
                <h3 className="font-heading text-base font-medium text-brand-dark mb-2">{v.title}</h3>
                <p className="font-body text-brand-dark/50 text-sm leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice */}
      <section className="bg-brand-light py-20 px-6" data-testid="about-practice">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">The Practice</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-brand-dark mb-6">
            {about.practice_heading}
          </h2>
          <div className="font-body text-brand-dark/60 text-base leading-relaxed space-y-4 max-w-3xl" data-testid="about-practice-text">
            <Paragraphs text={about.practice_text} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {[
              { value: 'SACAP', label: 'Registered' },
              { value: '9', label: 'Provinces Served' },
              { value: 'Residential', label: 'Primary Focus' },
              { value: 'Gauteng', label: 'Based In' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-xl font-medium text-brand-accent-dark">{s.value}</p>
                <p className="text-brand-dark/40 text-xs font-body mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-brand-accent/60 font-heading italic text-sm mb-4">We create space — by removing uncertainty before design begins.</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-white mb-6">
            Ready to explore your project?
          </h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/feasibility" data-testid="about-cta-feasibility">
              <Button className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-11 px-8 font-body text-sm font-semibold uppercase tracking-wider">
                Check Feasibility <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/projects" data-testid="about-cta-projects">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-sm h-11 px-8 font-body text-sm font-semibold uppercase tracking-wider">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
